import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Star, Calendar, Users, Clock, MapPin, Check, 
  Sparkles, Sun, Moon, Heart, Leaf, Music, ChevronDown
} from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";

// SEO Meta Component
const SEOMeta = () => {
  useEffect(() => {
    document.title = "Yoga Retreats UK 2026 | Yoga Holidays & Retreat Experiences";
    
    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = "Discover transformative yoga retreats across the UK and abroad with Annelie Lily. Day retreats in Somerset, weekend yoga breaks, and international yoga";

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://annelielily.com/retreats/yoga-retreats-uk";

    // Open Graph
    const ogTags = {
      'og:title': 'Yoga Retreats UK 2026 | Yoga Holidays & Retreat Experiences',
      'og:description': 'Transformative yoga retreats across the UK and abroad. Day retreats, weekend breaks, and international yoga holidays with Annelie Lily.',
      'og:url': 'https://annelielily.com/retreats/yoga-retreats-uk',
      'og:type': 'website',
      'og:image': 'https://customer-assets.emergentagent.com/job_yoga-frome/artifacts/r83k2965_image.png'
    };

    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);

  return null;
};

// Schema markup for the page
const SchemaMarkup = () => {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TouristTrip",
        "name": "Yoga Retreats UK with Annelie Lily",
        "description": "Transformative yoga retreats across the UK and abroad featuring Kundalini yoga, sound healing, meditation, and holistic wellness experiences.",
        "touristType": ["Wellness Tourism", "Yoga Tourism", "Spiritual Tourism"],
        "provider": {
          "@type": "LocalBusiness",
          "name": "Annelie Lily Yoga",
          "url": "https://annelielily.com",
          "telephone": "07540995021",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Little Keyford Lane",
            "addressLocality": "Frome",
            "addressRegion": "Somerset",
            "postalCode": "BA11 5BA",
            "addressCountry": "GB"
          }
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Are yoga retreats suitable for beginners?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Absolutely. Our retreats welcome all levels, from complete beginners to experienced practitioners. Each session offers modifications, and there's no pressure to do everything - rest is always an option."
            }
          },
          {
            "@type": "Question",
            "name": "What should I pack for a yoga retreat?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Comfortable yoga clothes, layers for varying temperatures, a water bottle, journal, and swimwear if sauna is included. Yoga mats and props are provided. We send a detailed packing list before each retreat."
            }
          },
          {
            "@type": "Question",
            "name": "How much do yoga retreats cost in the UK?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our day retreats start from £85-95, weekend retreats from £250-400, and international retreats vary by destination. All retreats include yoga sessions, meals, and activities as specified."
            }
          },
          {
            "@type": "Question",
            "name": "Can I come to a yoga retreat alone?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, many of our guests come solo. Retreats are a wonderful way to meet like-minded people in a supportive environment. You'll be welcomed warmly whether you come alone or with friends."
            }
          },
          {
            "@type": "Question",
            "name": "What styles of yoga are offered on retreats?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our retreats feature a blend of Kundalini yoga, Hatha, Yin, and restorative practices. We also incorporate breathwork, meditation, sound healing, and yoga nidra for a complete experience."
            }
          }
        ]
      },
      {
        "@type": "Event",
        "name": "Yoga Day Retreat at The Station, Frome",
        "description": "A full day of yoga, sound healing, sauna, and nourishing food at The Station in Frome, Somerset.",
        "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
        "eventStatus": "https://schema.org/EventScheduled",
        "location": {
          "@type": "Place",
          "name": "The Station, Frome",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Frome",
            "addressRegion": "Somerset",
            "addressCountry": "GB"
          }
        },
        "organizer": {
          "@type": "Person",
          "name": "Annelie Lily",
          "url": "https://annelielily.com"
        },
        "offers": {
          "@type": "Offer",
          "price": "85",
          "priceCurrency": "GBP",
          "availability": "https://schema.org/InStock"
        }
      }
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Hero Section
const HeroSection = () => (
  <section className="pt-8 pb-20 px-6 md:px-12 bg-gradient-to-b from-muted to-cream relative overflow-hidden">
    <div className="absolute inset-0 opacity-5">
      <div className="absolute top-20 left-10 w-64 h-64 bg-gold rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold rounded-full blur-3xl" />
    </div>
    
    <div className="max-w-4xl mx-auto text-center relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-4 block">
          Yoga Retreats & Holidays UK
        </span>
        <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-6 leading-tight">
          Yoga Retreats UK 2026
        </h1>
        <p className="text-lg md:text-xl text-neutral-700 max-w-2xl mx-auto mb-8 leading-relaxed">
          Step away from everyday life and immerse yourself in transformative yoga retreat experiences. 
          From day retreats in Somerset to international yoga holidays, find the space to rest, 
          reconnect, and renew.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
          <a href="#book" className="btn-primary px-8 py-3.5">
            View Dates & Book <ArrowRight className="w-4 h-4 inline ml-2" />
          </a>
          <a href="#retreat-types" className="btn-secondary px-8 py-3.5">
            Explore Retreats
          </a>
        </div>
        
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-neutral-600">
          <span className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gold" />
            Small intimate groups
          </span>
          <span className="flex items-center gap-2">
            <Star className="w-4 h-4 text-gold" />
            All levels welcome
          </span>
          <span className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-gold" />
            Holistic experiences
          </span>
        </div>
      </motion.div>
    </div>
  </section>
);

// Introduction Section
const IntroSection = () => (
  <section className="py-16 px-6 md:px-12 bg-cream">
    <div className="max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
          Why I Run Yoga Retreats
        </h2>
        <div className="prose-content space-y-4 text-neutral-700">
          <p>
            After more than a decade of teaching yoga in Frome and the surrounding areas, I've seen 
            how much people benefit from having dedicated time away from their everyday responsibilities. 
            A yoga retreat offers something that a weekly class simply can't — <strong>complete immersion 
            in practices that nurture your body, calm your mind, and restore your energy</strong>.
          </p>
          <p>
            My retreats are intentionally small, usually limited to 12 people, because I believe 
            transformation happens in intimate spaces where everyone feels seen and supported. 
            Whether you join me for a day retreat at The Station in Frome, a weekend away in the 
            British countryside, or an international yoga holiday, you'll experience the same 
            warmth and personal attention I bring to all my teaching.
          </p>
          <p>
            These aren't just yoga holidays — they're opportunities to pause, breathe deeply, 
            and remember who you are beneath the busyness of life.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

// Retreat Types Section
const RetreatTypesSection = () => {
  const retreatTypes = [
    {
      title: "Day Retreats",
      subtitle: "Frome, Somerset",
      description: "A full day of restoration without leaving the UK. Our day retreats at The Station in Frome combine morning Kundalini yoga, sauna sessions, nourishing lunch, afternoon Yin yoga, and a closing sound bath. Perfect when you need a reset but can't get away for longer.",
      duration: "9:45am - 4:45pm",
      price: "From £85",
      features: ["Kundalini & Yin yoga", "Sauna & cold plunge", "Sound healing", "Vegetarian lunch"],
      icon: Sun,
      link: "/contact"
    },
    {
      title: "Weekend Retreats",
      subtitle: "UK Locations",
      description: "Two to three nights of deep rest in beautiful British locations. Weekend yoga retreats offer the chance to truly unwind with multiple yoga sessions, meditation, nature walks, wholesome food, and plenty of time for rest. Wake up to yoga, fall asleep restored.",
      duration: "Friday - Sunday/Monday",
      price: "From £295",
      features: ["Multiple yoga styles", "Meditation & breathwork", "Nature connection", "All meals included"],
      icon: Moon,
      link: "/contact"
    },
    {
      title: "Week-Long Retreats",
      subtitle: "UK & International",
      description: "Extended yoga holidays for deeper transformation. A full week allows you to truly drop into practice, shed accumulated stress, and experience lasting change. Past destinations have included Devon, Spain, and Portugal.",
      duration: "5-7 nights",
      price: "From £599",
      features: ["Daily yoga practice", "Workshops & ceremonies", "Excursions & activities", "Full board"],
      icon: Sparkles,
      link: "/contact"
    },
    {
      title: "International Yoga Holidays",
      subtitle: "Spain, Egypt & Beyond",
      description: "Combine yoga with travel and cultural exploration. Our international retreats take you to inspiring destinations where you can practice yoga in extraordinary settings, explore sacred sites, and experience new cultures — all while being held in a supportive group.",
      duration: "7-14 nights",
      price: "From £1,200",
      features: ["Yoga in stunning locations", "Cultural experiences", "Sacred site visits", "Like-minded community"],
      icon: MapPin,
      link: "/contact"
    }
  ];

  return (
    <section id="retreat-types" className="py-16 px-6 md:px-12 bg-muted">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
            Choose Your Experience
          </span>
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
            Types of Yoga Retreats
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            From local day retreats to international yoga holidays, find the retreat experience 
            that fits your schedule, budget, and intentions
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {retreatTypes.map((retreat, index) => (
            <motion.div
              key={retreat.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/80 p-6 md:p-8 rounded-2xl border border-gold/10 hover:border-gold/30 transition-all"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <retreat.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-heading text-xl text-neutral-800">{retreat.title}</h3>
                  <p className="text-gold text-sm">{retreat.subtitle}</p>
                </div>
              </div>
              
              <p className="text-neutral-600 mb-4 leading-relaxed">{retreat.description}</p>
              
              <div className="flex flex-wrap gap-4 text-sm text-neutral-500 mb-4">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" /> {retreat.duration}
                </span>
                <span className="font-medium text-gold">{retreat.price}</span>
              </div>
              
              <ul className="grid grid-cols-2 gap-2 mb-6">
                {retreat.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm text-neutral-600">
                    <Check className="w-4 h-4 text-gold flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <Link 
                to={retreat.link}
                className="text-gold font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all"
              >
                Register Interest <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// What's Included Section
const WhatsIncludedSection = () => {
  const inclusions = [
    {
      title: "Kundalini Yoga",
      description: "Dynamic practice combining movement, breathwork, mantra, and meditation to awaken energy and clarity",
      icon: Sparkles
    },
    {
      title: "Hatha & Yin Yoga",
      description: "Balancing active postures with deep, restorative stretches for complete body-mind harmony",
      icon: Leaf
    },
    {
      title: "Sound Healing",
      description: "Tibetan singing bowls, gongs, and crystal bowls create vibrational healing and deep relaxation",
      icon: Music
    },
    {
      title: "Meditation & Breathwork",
      description: "Guided practices to calm the mind, release stress, and cultivate inner peace",
      icon: Heart
    },
    {
      title: "Cacao Ceremony",
      description: "Sacred heart-opening ceremonies with ceremonial-grade cacao to deepen connection",
      icon: Moon
    },
    {
      title: "Nourishing Food",
      description: "Vegetarian meals prepared with love, catering to dietary requirements and preferences",
      icon: Sun
    }
  ];

  return (
    <section className="py-16 px-6 md:px-12 bg-cream">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
            Holistic Experiences
          </span>
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
            What's Included in Our Retreats
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            Every retreat weaves together multiple practices for a complete wellness experience. 
            Here's what you can expect on a typical Annelie Lily retreat.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {inclusions.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className="bg-white/70 p-6 rounded-xl border border-gold/10"
            >
              <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center mb-4">
                <item.icon className="w-5 h-5 text-gold" />
              </div>
              <h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-neutral-600 text-sm mb-4">
            Interested in specific practices? Explore our regular offerings:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/womens-circles" className="text-gold hover:underline text-sm">Women's Circles</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/meditation" className="text-gold hover:underline text-sm">Meditation Classes</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/treatments" className="text-gold hover:underline text-sm">Healing & Treatments</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Sample Schedule Section
const SampleScheduleSection = () => {
  const schedule = [
    { time: "9:45am", activity: "Arrival & Welcome", desc: "Settle in with herbal tea and meet your fellow retreatants" },
    { time: "10:00am", activity: "Cacao Ceremony", desc: "Heart-opening ceremony to set intentions for the day" },
    { time: "10:45am", activity: "Kundalini Yoga", desc: "Dynamic practice with breathwork, movement, and mantra" },
    { time: "12:00pm", activity: "Sauna & Cold Plunge", desc: "Contrast therapy at Wildcat Sauna for deep release" },
    { time: "1:00pm", activity: "Nourishing Lunch", desc: "Vegetarian feast at Lungi Babas, The Station" },
    { time: "2:15pm", activity: "Hatha/Yin Yoga", desc: "Slower practice to integrate the morning's energy" },
    { time: "3:30pm", activity: "Yoga Nidra", desc: "Guided deep relaxation and conscious sleep" },
    { time: "4:00pm", activity: "Sound Bath", desc: "Immersive sound healing with singing bowls and gongs" },
    { time: "4:30pm", activity: "Closing Circle", desc: "Share, integrate, and close the retreat together" },
  ];

  return (
    <section className="py-16 px-6 md:px-12 bg-muted">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
            What to Expect
          </span>
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
            Sample Day Retreat Schedule
          </h2>
          <p className="text-neutral-600 max-w-xl mx-auto">
            Here's what a typical day retreat at The Station in Frome looks like. 
            Longer retreats follow a similar rhythm with more spaciousness.
          </p>
        </motion.div>

        <div className="bg-white/80 rounded-2xl border border-gold/10 overflow-hidden">
          {schedule.map((item, index) => (
            <motion.div
              key={item.time}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className={`flex gap-4 p-4 md:p-5 ${index !== schedule.length - 1 ? 'border-b border-gold/10' : ''}`}
            >
              <div className="w-20 flex-shrink-0">
                <span className="text-gold font-medium text-sm">{item.time}</span>
              </div>
              <div>
                <h4 className="font-medium text-neutral-800 mb-1">{item.activity}</h4>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-neutral-500 text-sm mt-6">
          Day retreats at The Station, Frome, Somerset
        </p>
      </div>
    </section>
  );
};

// Who Retreats Are For Section
const WhoRetreatsAreForSection = () => (
  <section className="py-16 px-6 md:px-12 bg-cream">
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
            Is a Retreat Right for You?
          </span>
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
            Who Our Yoga Retreats Are For
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white/70 p-6 md:p-8 rounded-2xl border border-gold/10">
            <h3 className="font-heading text-xl text-neutral-800 mb-4">
              You Don't Need to Be an Experienced Yogi
            </h3>
            <div className="space-y-3 text-neutral-700">
              <p>
                <strong>Complete beginners are welcome.</strong> Our retreats are designed 
                to meet you where you are. Every practice offers modifications, and there's 
                absolutely no pressure to do everything — rest is always an option.
              </p>
              <p>
                If you've never meditated, never done a sound bath, or never heard of 
                Kundalini yoga, that's perfectly fine. You'll be guided through everything, 
                and the small group size means you'll receive personal attention.
              </p>
            </div>
          </div>

          <div>
            <h3 className="font-heading text-lg text-neutral-800 mb-4">
              Our retreats are perfect for those who:
            </h3>
            <ul className="space-y-3">
              {[
                "Need a proper reset but can't get away for a full week",
                "Feel exhausted, overwhelmed, or disconnected from themselves",
                "Want to deepen their yoga practice in an immersive setting",
                "Are curious about sound healing, breathwork, or meditation",
                "Prefer intimate groups over large commercial retreats",
                "Value authentic, heart-led experiences over luxury spa breaks",
                "Are looking for a supportive community of like-minded people",
                "Want to travel and practice yoga in inspiring locations"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="text-center mt-10">
          <p className="text-neutral-600 mb-4">
            New to yoga? Start with our beginner-friendly resources:
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/yoga-classes/beginner-yoga" className="btn-secondary">
              Beginner Yoga Guide
            </Link>
            <Link to="/retreats/what-to-expect" className="text-gold hover:underline inline-flex items-center gap-1">
              What to Expect at a Retreat <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// Locations Section
const LocationsSection = () => (
  <section className="py-16 px-6 md:px-12 bg-muted">
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-10"
      >
        <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
          Where We Retreat
        </span>
        <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
          Yoga Retreat Locations
        </h2>
      </motion.div>

      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white/80 p-6 md:p-8 rounded-2xl border border-gold/10"
        >
          <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
            <MapPin className="w-4 h-4" />
            Somerset, UK
          </div>
          <h3 className="font-heading text-xl text-neutral-800 mb-3">
            The Station, Frome — Day Retreats
          </h3>
          <p className="text-neutral-700 mb-4">
            Our home base for day retreats. The Station is a beautiful creative space in the 
            heart of Frome, featuring the Really Awesome Studio for yoga, Wildcat Sauna for 
            heat therapy, and Lungi Babas for nourishing vegetarian food. Everything in one 
            place, no travel between venues.
          </p>
          <p className="text-neutral-600 text-sm">
            <strong>Getting there:</strong> Frome is easily accessible from <Link to="/areas/yoga-bath" className="text-gold hover:underline">Bath</Link> (25 min), 
            {" "}<Link to="/yoga-bristol" className="text-gold hover:underline">Bristol</Link> (45 min), 
            and London (2 hours by train). Free parking available.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white/80 p-6 md:p-8 rounded-2xl border border-gold/10"
        >
          <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
            <MapPin className="w-4 h-4" />
            UK & International
          </div>
          <h3 className="font-heading text-xl text-neutral-800 mb-3">
            Weekend & Extended Retreats
          </h3>
          <p className="text-neutral-700 mb-4">
            For longer retreats, we venture to inspiring locations chosen for their natural 
            beauty, peaceful atmosphere, and quality accommodation. Past and planned destinations 
            include beautiful venues in Devon, Cornwall, the Cotswolds, Spain, Portugal, and a 
            special pilgrimage to Egypt planned for 2026.
          </p>
          <p className="text-neutral-600 text-sm">
            Specific locations are announced when retreat dates are released. Join the waitlist 
            to be notified first.
          </p>
        </motion.div>
      </div>

      <div className="text-center mt-8">
        <Link to="/contact" className="btn-primary">
          Register Interest for 2026 Retreats
        </Link>
      </div>
    </div>
  </section>
);

// Upcoming Retreats / Booking Section
const UpcomingRetreatsSection = () => {
  return (
    <section className="py-16 px-6 md:px-12 bg-cream" id="book">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
            Book Your Spot
          </span>
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
            Upcoming Retreats & Sessions
          </h2>
          <p className="text-neutral-600 max-w-xl mx-auto">
            To check upcoming retreat dates, workshops and special sessions, or to reserve your place, 
            please get in touch and Annelie will be happy to help.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg border border-gold/10 px-6 py-10 text-center"
        >
          <Calendar className="w-8 h-8 text-gold mx-auto mb-4" />
          <h3 className="font-heading text-xl text-neutral-800 mb-3">Enquire About Availability</h3>
          <p className="text-neutral-600 text-sm max-w-md mx-auto mb-6">
            Retreats run throughout the year in small, intimate groups. Send a quick message 
            with your preferred dates and Annelie will share what's coming up.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-white px-6 py-3 rounded-full font-medium transition-colors text-sm"
              data-testid="retreat-enquire-book-btn"
            >
              Enquire to Book
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a 
              href="tel:+447540995021"
              className="inline-flex items-center justify-center gap-2 border border-gold/30 hover:border-gold text-gold px-6 py-3 rounded-full font-medium transition-colors text-sm"
              data-testid="retreat-call-btn"
            >
              Call 07540 995021
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => (
  <section className="py-16 px-6 md:px-12 bg-cream">
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-10"
      >
        <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
          Investment
        </span>
        <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
          Yoga Retreat Pricing
        </h2>
        <p className="text-neutral-600 max-w-xl mx-auto">
          Transparent pricing with no hidden costs. All retreats include yoga sessions, 
          meals, and activities as specified.
        </p>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { type: "Day Retreat", price: "£85-95", includes: "All activities, sauna, lunch" },
          { type: "Weekend Retreat", price: "£295-450", includes: "2-3 nights, all meals, yoga" },
          { type: "Week Retreat", price: "£599-899", includes: "5-7 nights, full board, excursions" },
          { type: "International", price: "£1,200+", includes: "Flights not included" }
        ].map((item, i) => (
          <motion.div
            key={item.type}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="bg-white/70 p-5 rounded-xl border border-gold/10 text-center"
          >
            <p className="text-neutral-600 text-sm mb-1">{item.type}</p>
            <p className="font-heading text-2xl text-neutral-800 mb-2">{item.price}</p>
            <p className="text-neutral-500 text-xs">{item.includes}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 bg-gold/5 p-6 rounded-xl border border-gold/10">
        <h3 className="font-medium text-neutral-800 mb-3">What's typically included:</h3>
        <div className="grid sm:grid-cols-2 gap-2 text-sm text-neutral-600">
          {[
            "All yoga & meditation sessions",
            "Sound healing & ceremonies",
            "Meals (vegetarian/vegan options)",
            "Use of facilities (sauna, etc.)",
            "Props & mats provided",
            "Pre-retreat information pack"
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <Check className="w-4 h-4 text-gold" />
              {item}
            </div>
          ))}
        </div>
        <p className="text-neutral-500 text-xs mt-4">
          Payment plans available for longer retreats. Early bird discounts often apply.
        </p>
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      q: "Are yoga retreats suitable for beginners?",
      a: "Absolutely. Our retreats welcome all levels, from complete beginners to experienced practitioners. Each session offers modifications, and there's no pressure to do everything — rest is always an option. Many guests tell us their first retreat was the perfect introduction to yoga."
    },
    {
      q: "What should I pack for a yoga retreat?",
      a: "Comfortable yoga clothes in layers, a water bottle, journal, and any personal items you need. If sauna is included, bring swimwear. Yoga mats and props are provided. We send a detailed packing list before each retreat with specific recommendations."
    },
    {
      q: "Can I come to a yoga retreat alone?",
      a: "Yes! Many of our guests come solo, and it's a wonderful way to meet like-minded people. The small group size and supportive environment mean you'll quickly feel part of the community. Solo travellers are always welcome."
    },
    {
      q: "What if I have dietary requirements?",
      a: "All our meals are vegetarian, and we happily cater to vegan, gluten-free, and other dietary needs. Just let us know when booking, and we'll ensure you're well-fed with delicious, nourishing food."
    },
    {
      q: "How much yoga experience do I need?",
      a: "None at all! While experienced yogis will deepen their practice, complete beginners are equally welcome. The practices are accessible, and you'll be guided through everything. There's no 'keeping up' — you go at your own pace."
    },
    {
      q: "What styles of yoga are offered?",
      a: "Our retreats typically blend Kundalini yoga (dynamic, energising), Hatha yoga (balanced postures), and Yin yoga (slow, restorative). We also incorporate breathwork, meditation, yoga nidra, and sound healing for a complete experience."
    },
    {
      q: "Are there single rooms available?",
      a: "For residential retreats, we offer both shared and single room options where possible. Single rooms typically have a supplement. Day retreats don't require accommodation. Specific options are listed for each retreat."
    },
    {
      q: "What's your cancellation policy?",
      a: "Deposits are typically non-refundable but may be transferred to future retreats. Full payment is usually required 4-6 weeks before. If you need to cancel after this, we'll try to fill your space — if successful, we can offer a partial refund or credit."
    },
    {
      q: "How do I book a retreat?",
      a: "Start by registering your interest via our contact form. For scheduled retreats, you'll receive booking information via email. A deposit secures your place, with the balance due before the retreat. We're happy to answer any questions first."
    },
    {
      q: "Do you offer gift vouchers for retreats?",
      a: "Yes! A yoga retreat makes a meaningful gift. Contact us to arrange a gift voucher for a specific retreat or a monetary amount towards a future retreat of the recipient's choice."
    }
  ];

  return (
    <section className="py-16 px-6 md:px-12 bg-muted">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
            Your Questions Answered
          </span>
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
            Yoga Retreat FAQs
          </h2>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.details
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.03 }}
              className="group bg-white/80 rounded-xl border border-gold/10 overflow-hidden"
            >
              <summary className="p-5 cursor-pointer flex items-center justify-between font-medium text-neutral-800 hover:text-gold transition-colors">
                {faq.q}
                <ChevronDown className="w-5 h-5 text-gold group-open:rotate-180 transition-transform" />
              </summary>
              <div className="px-5 pb-5 text-neutral-600 leading-relaxed">
                {faq.a}
              </div>
            </motion.details>
          ))}
        </div>

        <div className="text-center mt-8">
          <p className="text-neutral-600 mb-4">Still have questions?</p>
          <Link to="/contact" className="btn-primary">
            Get in Touch
          </Link>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "The retreat was exactly what I needed. A full day of nourishment for body and soul. The combination of yoga, sauna, sound bath and beautiful food left me feeling completely renewed. Annelie creates such a warm, welcoming space — I can't wait for the next one.",
      name: "Sarah",
      type: "Day Retreat, Frome"
    },
    {
      quote: "I went to the retreat day and it was amazing. I got emotional and let go of a huge load of stuff. I can't recommend Annelie enough. I really do mean it when I say you are helping me to make my life better.",
      name: "Joanne Harry",
      type: "Day Retreat"
    },
    {
      quote: "As someone who had never done yoga before, I was nervous about joining a retreat. But Annelie made everyone feel welcome regardless of experience. The weekend was transformative — I left feeling like a different person.",
      name: "Mark",
      type: "Weekend Retreat"
    }
  ];

  return (
    <section className="py-16 px-6 md:px-12 bg-cream">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
            Guest Experiences
          </span>
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
            What Retreat Guests Say
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/70 p-6 rounded-2xl border border-gold/10"
            >
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-gold text-gold" />
                ))}
              </div>
              <p className="text-neutral-700 italic mb-4 leading-relaxed">"{t.quote}"</p>
              <div className="border-t border-gold/10 pt-4">
                <p className="font-medium text-neutral-800">{t.name}</p>
                <p className="text-neutral-500 text-sm">{t.type}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section className="py-20 px-6 md:px-12 bg-neutral-800 text-white">
    <div className="max-w-3xl mx-auto text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="font-heading text-3xl md:text-4xl mb-4">
          Ready to Step Away and Reconnect?
        </h2>
        <p className="text-neutral-300 mb-8 max-w-xl mx-auto">
          Join the waitlist for 2026 retreats, or get in touch to discuss which 
          retreat experience might be right for you.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact" className="bg-gold hover:bg-gold-dark text-white px-8 py-3.5 rounded-full font-medium transition-colors">
            Register Interest
          </Link>
          <a href="tel:07540995021" className="border border-white/30 hover:border-white text-white px-8 py-3.5 rounded-full font-medium transition-colors">
            Call 07540 995021
          </a>
        </div>
        <p className="text-neutral-500 text-sm mt-6">
          Based in Frome, Somerset • Serving the UK and beyond
        </p>
      </motion.div>
    </div>
  </section>
);

// Related Links Section (Internal Linking)
const RelatedLinksSection = () => (
  <section className="py-12 px-6 md:px-12 bg-muted">
    <div className="max-w-4xl mx-auto">
      <h2 className="font-heading text-xl text-neutral-800 mb-6 text-center">
        Explore More
      </h2>
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/retreats/what-to-expect" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
          <span className="text-neutral-800 font-medium text-sm">What to Expect</span>
          <p className="text-neutral-500 text-xs mt-1">First retreat guide</p>
        </Link>
        <Link to="/retreats/packing-list" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
          <span className="text-neutral-800 font-medium text-sm">Packing List</span>
          <p className="text-neutral-500 text-xs mt-1">What to bring</p>
        </Link>
        <Link to="/retreats/benefits" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
          <span className="text-neutral-800 font-medium text-sm">Retreat Benefits</span>
          <p className="text-neutral-500 text-xs mt-1">Why go on retreat</p>
        </Link>
        <Link to="/retreats/sound-healing" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
          <span className="text-neutral-800 font-medium text-sm">Sound Healing</span>
          <p className="text-neutral-500 text-xs mt-1">Explore sound baths</p>
        </Link>
      </div>
    </div>
  </section>
);

// Main Page Component
const YogaRetreatsUK = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-cream">
      <SEOMeta />
      <SchemaMarkup />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga Retreats UK" currentSlug="yoga-retreats-uk" />
      
      <main>
        <HeroSection />
        <IntroSection />
        <RetreatTypesSection />
        <UpcomingRetreatsSection />
        <TestimonialsSection />
        <WhatsIncludedSection />
        <SampleScheduleSection />
        <WhoRetreatsAreForSection />
        <LocationsSection />
        <PricingSection />
        <FAQSection />
        <CTASection />
        <RelatedLinksSection />
      </main>
      
      <Footer />
    </div>
  );
};

export default YogaRetreatsUK;
